import React, { useEffect, useCallback, useRef, useState } from 'react';
import type { Node } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import map from 'lodash/map';
import get from 'lodash/get';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import { FormSpy } from 'react-final-form';
import { Icon, Input, Field, Title, Text, IconButton } from '@vmos2/design-system';
import { ClickOutside, Form } from '@vmos2/utils';

import { useLocation } from './useLocation';
import { useAddressesList } from './useAddressesList';
import { AddressLookupHighlitedLabel } from './addressLookupHighlitedLabel';
import { GoogleMapsProvider } from './GoogleMapsProvider';
import { GeoLocationButton } from './geoLocationButton';
import { validate } from './validator';

type Coordinates = {
  latitude: number,
  longitude: number,
};

type GeocodeAddress = Coordinates & {
  id: string,
  label: string,
};

type Props = {
  disableUseMyLocation?: boolean,
  preselectAddress?: boolean,
  autoFocus?: boolean,
  userAddress?: string,
  onPostCodeChange?: Function,
  alertIfAddressNotFound?: boolean,
  addScrollEffect?: boolean,
  handleSelectAddress?: Function,
  errorText?: string,
};

export const AddressesLookup = ({
  disableUseMyLocation,
  preselectAddress = true,
  autoFocus,
  userAddress,
  onPostCodeChange,
  alertIfAddressNotFound,
  addScrollEffect,
  handleSelectAddress,
  errorText,
}: Props): Node => {
  const googleMap = useRef();
  const inputRef = useRef();
  const theme = useTheme();
  const [searchText, setSearchText] = useState(null);
  const [isAddressSelected, setAddressSelected] = useState(false);
  const { addresses, fetchAddresses, isLoading, isNotFound, resetAddresses } = useAddressesList();

  const { t } = useTranslation('geoLocation');
  const {
    location,
    postCode,
    getNavigatorGeolocation,
    resetLocation,
    googleApiKey,
    changeLocation,
    saveLocalStorageLocation,
    deniedAccess,
    setDeniedAccess,
  } = useLocation();

  const scrollToInputCenter = () => inputRef.current.scrollIntoView({ block: 'center' });

  useEffect(() => {
    if (isNotFound) resetLocation();
  }, [isNotFound, resetLocation]);

  const selectAddress = useCallback(
    async (address: GeocodeAddress) => {
      const [postCode, location] = await changeLocation(address, googleMap);
      if (handleSelectAddress) {
        handleSelectAddress({ postCode, location });
        setSearchText('');
        setAddressSelected(true);
      } else {
        saveLocalStorageLocation(postCode, location);
      }
      if (postCode && onPostCodeChange) onPostCodeChange(postCode);
      resetAddresses();
    },
    [changeLocation, saveLocalStorageLocation, handleSelectAddress, onPostCodeChange, resetAddresses]
  );

  useEffect(() => {
    if (!location && userAddress && preselectAddress) {
      setSearchText(userAddress);
      fetchAddresses(userAddress);
    }
  }, [userAddress, location, preselectAddress]);

  useEffect(() => {
    const searchTextLabel = get(location, 'label', postCode);
    if (searchTextLabel && !get(location, 'isNavigatorGeolocation') && !searchText && !deniedAccess && preselectAddress) {
      setSearchText(searchTextLabel);
    }
  }, [location, postCode, deniedAccess, searchText, preselectAddress]);

  const suffix = useCallback(
    (form: { reset: Function }) => {
      if (!isLoading && searchText) {
        return (
          <IconButton
            icon="CrossCircle"
            mode="ghost"
            block
            onClick={() => {
              resetAddresses();
              resetLocation();
              setSearchText(null);
              form.reset();
            }}
          />
        );
      }
      return null;
    },
    [isLoading, resetAddresses, resetLocation, searchText]
  );

  return (
    <GoogleMapsProvider apiKey={googleApiKey}>
      <div ref={googleMap} id="googleMap" />
      <Wrapper
        name="searchAddress"
        id="searchAddress"
        validate={values => validate(values, deniedAccess, '', t, location?.isNavigatorGeolocation, isAddressSelected)}
        initialValues={{ searchText }}
        render={({ handleSubmit, form, submitting }) => {
          return (
            <form onSubmit={handleSubmit}>
              <TextFieldWrapper onClickOutside={resetAddresses}>
                <AddressField
                  ref={inputRef}
                  error={!!errorText && !isNull(searchText) && errorText}
                  onFocus={() => addScrollEffect && scrollToInputCenter()}
                  placeholder={get(location, 'isNavigatorGeolocation') ? t('currentLocation') : t('enterYourAddress')}
                  activeGeolocation={!!get(location, 'isNavigatorGeolocation')}
                  component={Input}
                  size="small"
                  suffix={suffix(form)}
                  name="searchText"
                  fullWidth
                  autoComplete="off"
                  autoFocus={autoFocus}
                  loading={isLoading}
                  handleChange={text => {
                    fetchAddresses(text);
                    setDeniedAccess(null);
                  }}
                  onClick={e => (!isEmpty(e.target.value) ? fetchAddresses(e.target.value) : noop)}
                />
                {!submitting && addresses.length ? (
                  <AddressesList>
                    {map(addresses, address => (
                      <Address
                        key={address.id}
                        onClick={() => {
                          selectAddress({ ...address });
                          setSearchText(address.label);
                        }}
                      >
                        <FormSpy subscription={{ values: true }}>
                          {({ values }) => (
                            <AddressLookupHighlitedLabel label={address.label} searchText={get(values, 'searchText')} />
                          )}
                        </FormSpy>
                      </Address>
                    ))}
                  </AddressesList>
                ) : null}
              </TextFieldWrapper>
              <GeoLocationButton
                disableMyLocation={disableUseMyLocation}
                form={form}
                onClick={() => setSearchText(null)}
                getNavigatorGeolocation={getNavigatorGeolocation}
              />
            </form>
          );
        }}
      />
      {alertIfAddressNotFound && isNotFound && (
        <NoAddressesFound>
          <IconWrapper fill={theme.color.lightGrey} name="Search" size={80} />
          <Title level={2} mb={2}>
            {t('storeSelection:noAddressesFound')}
          </Title>
          <Text color={theme.color.grey} level={3}>
            {t('storeSelection:correctAddressSpelling')}
          </Text>
        </NoAddressesFound>
      )}
    </GoogleMapsProvider>
  );
};

const Wrapper = styled(Form)`
  display: inherit;
  background-color: ${({ theme }) => theme.color.white};
`;

const IconWrapper = styled(Icon)`
  margin-bottom: 24px;
`;

const AddressField = styled(Field)`
  & + span {
    background: ${({ theme }) => theme.color.white};
    border-radius: inherit;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  &:focus {
    box-shadow: none;
  }
  &::placeholder {
    color: ${({ activeGeolocation, theme }) => activeGeolocation && theme.color.brand};
  }
`;

const AddressesList = styled('div')`
  position: absolute;
  width: 100%;
  border: ${({ theme }) => theme.stroke.brand};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.fontSize.md};
  background-color: ${({ theme }) => theme.color.white};
  z-index: 1;
`;

const Address = styled('div')`
  height: 44px;
  line-height: 44px;
  padding-left: 16px;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  &:hover {
    background-color: ${({ theme }) => theme.color.lighterGrey};
    cursor: pointer;
  }
`;

const TextFieldWrapper = styled(ClickOutside)`
  position: relative;
  flex: 1;
`;

const NoAddressesFound = styled('div')`
  text-align: center;
  margin-top: 24px;
`;
